import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import PercentageInputCell from './PercentageInputCell';
import CurrencyInputCell from './CurrencyInputCell';
import StringInputCell from './StringInputCell';
import {
  convertDecimalToPercentage,
  convertDecimalToCurrency,
  getStylesBasedOnVariant,
  getBorderStyles,
} from './utils';
import { TableCellVariant } from './types';

interface TableCellProps {
  value: number | string | null;
  isEditable: boolean;
  isDisabled: boolean;
  variant: TableCellVariant;
  row: number;
  col: number;
  format?: 'percentage' | 'currency' | 'string' | 'number';
  x: number;
  y: number;
  width: number;
  height: number;
  onChange: (value: number | string | null, row: number, col: number) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>, row: number, col: number) => void;
  onFocus?: (row: number, col: number) => void;
  onBlur?: () => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>, row: number, col: number) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>, row: number, col: number) => void;
  onMouseOver?: (event: React.MouseEvent<HTMLDivElement>, row: number, col: number) => void;
  onMouseUp?: () => void;
  isFocused?: boolean;
  sx?: SxProps<Theme>;
  isLeftEdge?: boolean;
  isRightEdge?: boolean;
  isBottomEdge?: boolean;
  isError?: boolean;
  isSelected?: boolean;
  isCopied?: boolean;
  isPasted?: boolean;
  autofocus?: boolean;
  isSelectionTopEdge?: boolean;
  isSelectionBottomEdge?: boolean;
  isSelectionLeftEdge?: boolean;
  isSelectionRightEdge?: boolean;
}

const baseCellStyles = {
  backgroundColor: 'white',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 600,
  color: '#1B1C17',
};

const disabledCellStyles = {
  backgroundColor: '#F0F0F1',
};

const styleResolvers = (
  variant: TableCellVariant,
): { textAlign: 'center' | 'right' | 'left'; padding: string } => {
  switch (variant) {
    case 'percentage-gray':
    case 'percentage-disabled':
    case 'percentage-editable':
    case 'percentage-disabled-light':
      return { textAlign: 'right', padding: '0 8px 0 0' };
    case 'fixed-light':
      return { textAlign: 'left', padding: '0 0 0 8px' };
    default:
      return { textAlign: 'center', padding: '0px' };
  }
};

const TableCell: React.FC<TableCellProps> = ({
  value,
  isEditable,
  isDisabled,
  variant,
  row,
  col,
  format = 'string',
  x,
  y,
  width,
  height,
  onChange,
  onPaste,
  onFocus,
  onBlur,
  onClick,
  onMouseDown,
  onMouseOver,
  onMouseUp,
  isFocused = false,
  sx = {},
  isLeftEdge = false,
  isRightEdge = false,
  isBottomEdge = false,
  isError = false,
  isSelected = false,
  isCopied = false,
  isPasted = false,
  autofocus = false,
  isSelectionTopEdge = false,
  isSelectionBottomEdge = false,
  isSelectionLeftEdge = false,
  isSelectionRightEdge = false,
}) => {
  const handleFocus = () => {
    if (onFocus) {
      onFocus(row, col);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      onClick(event, row, col);
    }
    // Also trigger focus for non-editable or disabled cells when clicked
    if ((!isEditable || isDisabled) && onFocus) {
      handleFocus();
    }
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onMouseDown) {
      onMouseDown(event, row, col);
    }
    // Prevent text selection on non-editable cells when dragging
    if (!isEditable) {
      event.preventDefault();
      if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
      }
    }
  };

  const handleMouseOver = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onMouseOver) {
      onMouseOver(event, row, col);
    }
  };

  const handleMouseUp = () => {
    if (onMouseUp) {
      onMouseUp();
    }
  };

  const renderCellContent = () => {
    if (isDisabled) {
      return null;
    }

    if (isEditable) {
      switch (format) {
        case 'percentage':
          return (
            <PercentageInputCell
              value={value as number | null}
              isEditable={isEditable}
              row={row}
              col={col}
              onChange={onChange}
              onPaste={onPaste}
              onFocus={handleFocus}
              onBlur={handleBlur}
              variant={variant}
              isSelected={isSelected}
              isCopied={isCopied}
              isPasted={isPasted}
              autofocus={autofocus}
            />
          );
        case 'currency':
          return (
            <CurrencyInputCell
              value={value as number | null}
              row={row}
              col={col}
              onChange={onChange}
              onPaste={onPaste}
              onFocus={handleFocus}
              onBlur={handleBlur}
              variant={variant}
              isSelected={isSelected}
              isCopied={isCopied}
              isPasted={isPasted}
            />
          );
        case 'number':
          return (
            <PercentageInputCell
              value={value as number | null}
              isEditable={isEditable}
              row={row}
              col={col}
              onChange={onChange}
              onPaste={onPaste}
              onFocus={handleFocus}
              onBlur={handleBlur}
              variant={variant}
              isSelected={isSelected}
              isCopied={isCopied}
              isPasted={isPasted}
              autofocus={autofocus}
              shouldFormatValue={false}
            />
          );
        case 'string':
        default:
          return (
            <StringInputCell
              value={value}
              row={row}
              col={col}
              onChange={onChange}
              onPaste={onPaste}
              onFocus={handleFocus}
              onBlur={handleBlur}
              variant={variant}
              isSelected={isSelected}
              isCopied={isCopied}
              isPasted={isPasted}
            />
          );
      }
    }

    // Format display-only cells based on their format type
    if (value === null || value === undefined) {
      return '';
    }

    switch (format) {
      case 'percentage':
        return (
          <input
            readOnly
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              outline: 'none',
              textAlign: styleResolvers(variant).textAlign,
              padding: styleResolvers(variant).padding,
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 600,
              userSelect: 'none',
              color: '#162936',
              ...getStylesBasedOnVariant(variant),
              ...(isSelected && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
              ...(isCopied && {
                outline: '2px dashed #2F736E',
                outlineOffset: '-3px',
                backgroundColor: '#2F736E2F',
              }),
              ...(variant === 'primary-black' && {
                backgroundColor: 'transparent',
              }),
            }}
            value={typeof value === 'number' ? convertDecimalToPercentage(value) : value}
          />
        );
      case 'currency':
        return typeof value === 'number' ? convertDecimalToCurrency(value) : value;
      case 'string':
        return (
          <input
            readOnly
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              outline: 'none',
              textAlign: styleResolvers(variant).textAlign,
              padding: styleResolvers(variant).padding,
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: 600,
              userSelect: 'none',
              color: '#162936',
              ...getStylesBasedOnVariant(variant),
              ...(isSelected && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
              ...(isCopied && {
                outline: '2px dashed #2F736E',
                outlineOffset: '-3px',
                backgroundColor: '#2F736E2F',
              }),
              ...(variant === 'primary-black' && {
                backgroundColor: 'transparent',
              }),
            }}
            value={value}
          />
        );
      default:
        return value;
    }
  };

  return (
    <Box
      id={`cell-${row}-${col}`}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseOver={handleMouseOver}
      onMouseUp={handleMouseUp}
      sx={{
        position: 'absolute',
        left: x,
        top: y,
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...baseCellStyles,
        ...getBorderStyles(
          isError,
          isFocused,
          isLeftEdge,
          isRightEdge,
          isBottomEdge,
          isSelected,
          isSelectionTopEdge,
          isSelectionBottomEdge,
          isSelectionLeftEdge,
          isSelectionRightEdge,
        ),
        ...(isDisabled && disabledCellStyles),
        ...sx,
        ...getStylesBasedOnVariant(variant),
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {renderCellContent()}
    </Box>
  );
};

export default TableCell;
